import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, MousePointerClick, TrendingUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div className="border border-white/10 bg-[#111827] p-6" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-500">
      <Icon size={15} style={{ color }} />
      <span className="text-xs font-mono2 uppercase tracking-[0.15em]">{label}</span>
    </div>
    <div className="mt-3 font-heading text-4xl font-extrabold" style={{ color }}>{value}</div>
  </div>
);

const RankList = ({ title, rows, empty, showRate }) => (
  <div className="border border-white/10 bg-[#111827] p-6">
    <h3 className="font-heading text-base font-semibold text-slate-100 mb-4">{title}</h3>
    {(!rows || rows.length === 0) && <p className="text-sm text-slate-500">{empty}</p>}
    <div className="space-y-2">
      {rows?.map((r) => (
        <div key={r.name} className="flex items-center justify-between text-sm">
          <span className="text-slate-300 truncate mr-3">{r.name}</span>
          <span className="font-mono2 text-teal-300 shrink-0">
            {r.count}{showRate && <span className="text-slate-500"> · {r.rate}%</span>}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export default function AdminStats() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm text-slate-500" data-testid="admin-stats-loading">Chargement des statistiques…</p>;

  const series = (data.series || []).map((d) => ({ ...d, label: d.date.slice(5).split("-").reverse().join("/") }));

  return (
    <div className="space-y-6" data-testid="admin-stats">
      <div className="grid gap-5 sm:grid-cols-3 max-w-3xl">
        <StatCard icon={Eye} label="Vues de pages" value={data.total_pageviews} color="#2dd4bf" testid="stat-pageviews" />
        <StatCard icon={MousePointerClick} label="Clics" value={data.total_clicks} color="#fbbf24" testid="stat-clicks" />
        <StatCard icon={TrendingUp} label="Taux de conversion" value={`${data.conversion_rate ?? 0}%`} color="#a78bfa" testid="stat-conversion" />
      </div>

      <div className="border border-white/10 bg-[#111827] p-6">
        <h3 className="font-heading text-base font-semibold text-slate-100 mb-5">30 derniers jours</h3>
        <div style={{ width: "100%", height: 280 }} data-testid="stats-chart">
          <ResponsiveContainer>
            <AreaChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gpv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2dd4bf" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#2dd4bf" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gcl" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#fbbf24" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} interval={4} />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0d1322", border: "1px solid #1e293b", borderRadius: 8, color: "#e2e8f0" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="pageviews" name="Vues" stroke="#2dd4bf" fill="url(#gpv)" strokeWidth={2} />
              <Area type="monotone" dataKey="clicks" name="Clics" stroke="#fbbf24" fill="url(#gcl)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <RankList title="Pages les plus vues" rows={data.pages} empty="Aucune vue enregistrée pour l'instant." />
        <RankList title="Clics par bouton" rows={data.ctas} empty="Aucun clic enregistré pour l'instant." showRate />
      </div>
    </div>
  );
}
